import React, { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

// PortalMenu — render a dropdown menu attached to a trigger element,
// portalled into document.body so ancestor overflow:hidden can't clip
// it. Walter 2026-05-17: the overflow-menu inside list tiles was
// getting cut off when the tile sat near the viewport edge with a
// narrow browser window.
//
// Behavior:
// - Position is `fixed`, computed from the trigger's bounding rect.
// - The menu anchors its top-right corner to the trigger's
//   bottom-right corner by default.
// - If the menu would overflow the bottom of the viewport, it flips
//   above the trigger (top-right anchored to trigger's top-right).
// - If the menu would overflow the right edge, it slides left so the
//   right edge touches the viewport edge (with an 8px margin).
//
// Caller controls visibility via `open`. The portalled element is
// only mounted while `open === true`, so no measurement cost
// otherwise.
//
// Usage:
//   const triggerRef = useRef(null);
//   <button ref={triggerRef} onClick={() => setOpen(true)}>⋯</button>
//   <PortalMenu open={open} triggerRef={triggerRef} onClose={() => setOpen(false)}>
//     <MenuRow>…</MenuRow>
//   </PortalMenu>

export default function PortalMenu({
  open,
  triggerRef,
  onClose,
  children,
  minWidth = 150,
  maxHeight = 320,
  zIndex = 60,
  // Walter 2026-07-02: 'visible' lets a child SubMenu fly-out escape the menu
  // box horizontally (the default 'auto' forces overflow-x to clip). Menus
  // that use fly-outs pass 'visible' and stay short enough not to need scroll.
  overflow = 'auto',
}) {
  // We re-measure on every open and on window resize while open.
  const [pos, setPos] = useState(null);
  const menuRef = useRef(null);

  useLayoutEffect(() => {
    if (!open) return undefined;
    const measure = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const tr = trigger.getBoundingClientRect();
      const menu = menuRef.current;
      // First-pass: use minWidth / maxHeight as size hints for
      // overflow checks. After mount we re-measure with the actual
      // rendered size for a snug fit.
      const mw = menu?.offsetWidth ?? minWidth;
      const mh = menu?.offsetHeight ?? maxHeight;
      const vh = window.innerHeight;
      const vw = window.innerWidth;

      // Vertical: default below; flip above if it would overflow bottom.
      let top = tr.bottom + 4;
      const overflowsBottom = top + mh > vh - 8;
      const fitsAbove = tr.top - 4 - mh >= 8;
      if (overflowsBottom && fitsAbove) {
        top = tr.top - 4 - mh;
      } else if (overflowsBottom) {
        // Neither fits cleanly — pin to the bottom edge.
        top = Math.max(8, vh - mh - 8);
      }

      // Horizontal: align right edge with trigger's right edge by
      // default. If that pushes the left past the viewport's left
      // edge (very narrow menu containers in very narrow viewports),
      // snap to left:8.
      let left = tr.right - mw;
      if (left < 8) left = 8;
      // If trigger is so far right that menu would overflow right,
      // snap to right edge with an 8px margin.
      if (left + mw > vw - 8) left = vw - mw - 8;

      setPos({ top, left });
    };
    measure();
    // Re-measure once after mount with actual rendered size.
    const id = requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, triggerRef, minWidth, maxHeight]);

  // Outside-click dismiss. Capture-phase + pointer/touch so it works
  // with touch and beats any stopPropagation in a descendant.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      const m = menuRef.current;
      const t = triggerRef.current;
      if (m && !m.contains(e.target) && t && !t.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('touchstart', onDown, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('touchstart', onDown, true);
    };
  }, [open, triggerRef, onClose]);

  if (!open) return null;

  // While we're measuring (pos === null), render off-screen so the
  // first paint doesn't flash at (0,0) before useLayoutEffect runs.
  const style = pos
    ? { position: 'fixed', top: pos.top, left: pos.left }
    : { position: 'fixed', top: -9999, left: -9999 };

  return createPortal(
    <div
      ref={menuRef}
      style={{
        ...style,
        minWidth,
        // When overflow is visible we must NOT clamp the height, or offsetHeight
        // (used for the flip math above) would read the clamp instead of the
        // real content height and mis-place the menu.
        maxHeight: overflow === 'visible' ? undefined : maxHeight,
        overflowY: overflow,
        background: 'var(--surface)',
        border: '1px solid var(--border-bright)',
        borderRadius: 'var(--radius)',
        boxShadow: 'var(--shadow-menu)',
        zIndex,
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
